import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { AlertTriangle } from "lucide-react";

// Nagpur city centre
const NAGPUR_CENTER = [21.1458, 79.0882];

function tone(escalated, total) {
  const ratio = total ? escalated / total : 0;
  if (ratio >= 0.5) return { color: "#dc2626", fill: "#ef4444" }; // red
  if (ratio >= 0.2) return { color: "#d97706", fill: "#f59e0b" }; // amber
  if (total > 0)     return { color: "#059669", fill: "#10b981" }; // green
  return { color: "#64748b", fill: "#94a3b8" };                    // slate (none)
}

function radiusFor(total, max) {
  if (!total) return 6;
  const base = 8;
  const growth = 22;
  return base + (Math.sqrt(total) / Math.sqrt(Math.max(max, 1))) * growth;
}

export default function ZoneHeatmap() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/heatmap").then((r) => setData(r.data)).catch(() => setData([]));
  }, []);

  const max = useMemo(() => (data || []).reduce((a, b) => Math.max(a, b.total || 0), 0), [data]);

  if (data === null) return <div className="p-4 text-muted-foreground text-sm">Loading map…</div>;

  const grandTotal = data.reduce((a, b) => a + (b.total || 0), 0);
  const grandEscalated = data.reduce((a, b) => a + (b.escalated || 0), 0);

  return (
    <div className="space-y-2" data-testid="zone-heatmap">
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
        <span className="overline text-slate-500">Legend</span>
        <LegendDot tone="bg-emerald-500" label="Low escalation" />
        <LegendDot tone="bg-amber-500" label="Medium" />
        <LegendDot tone="bg-red-500" label="High escalation" />
        <LegendDot tone="bg-slate-400" label="No complaints" />
        <span className="ml-auto text-muted-foreground">Circle size = complaint volume · Color = escalation ratio</span>
      </div>
      <div className="rounded-lg overflow-hidden border border-border">
        <MapContainer center={NAGPUR_CENTER} zoom={11} scrollWheelZoom={false} style={{ height: 460, width: "100%" }}>
          <TileLayer
            attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {data.map((st) => {
            if (st.lat == null || st.lng == null) return null;
            const t = tone(st.escalated, st.total);
            const r = radiusFor(st.total, max);
            return (
              <CircleMarker
                key={st.station_key}
                center={[st.lat, st.lng]}
                radius={r}
                pathOptions={{ color: t.color, fillColor: t.fill, fillOpacity: 0.55, weight: 1.5 }}
              >
                <Tooltip direction="top" offset={[0, -4]} opacity={0.95}>
                  <div style={{ fontWeight: 600 }}>{st.station_name}</div>
                  <div style={{ fontSize: 11, color: "#334155" }}>
                    Total {st.total} · Pending {st.pending} · Esc {st.escalated}
                  </div>
                </Tooltip>
                <Popup>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{st.station_name}</div>
                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>{st.acp} · {st.zone}</div>
                  <table style={{ fontSize: 11, borderCollapse: "collapse" }}>
                    <tbody>
                      <tr><td>Total</td><td style={{ paddingLeft: 8, fontWeight: 600 }}>{st.total}</td></tr>
                      <tr><td>Pending</td><td style={{ paddingLeft: 8, fontWeight: 600, color: "#b45309" }}>{st.pending}</td></tr>
                      <tr><td>Resolved</td><td style={{ paddingLeft: 8, fontWeight: 600, color: "#047857" }}>{st.resolved}</td></tr>
                      <tr><td>Escalated</td><td style={{ paddingLeft: 8, fontWeight: 600, color: "#b91c1c" }}>{st.escalated}</td></tr>
                      <tr><td>Rejected (fake)</td><td style={{ paddingLeft: 8, fontWeight: 600, color: "#475569" }}>{st.rejected}</td></tr>
                    </tbody>
                  </table>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        {grandEscalated > 0 && (
          <span className="inline-flex items-center gap-1 text-red-700">
            <AlertTriangle className="w-3 h-3" /> {grandEscalated} of {grandTotal} complaints escalated citywide
          </span>
        )}
      </div>
    </div>
  );
}

function LegendDot({ tone, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block w-2.5 h-2.5 rounded-full ${tone}`} />
      <span>{label}</span>
    </span>
  );
}
